"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import type { GalleryEvent } from "@/app/(site)/gallery/gallery-board";
import { openDonateModal } from "@/lib/donate";
import { readSession } from "@/lib/session";

type ProfileUser = {
  id?: number;
  username?: string;
  full_name?: string;
  role?: string;
};

function sessionLabel() {
  const session = readSession();
  const role = session?.role === "committee" ? "Committee" : "Member";
  return `${session?.full_name || ""}|${role}`;
}

export default function MemberDashboard({ albums }: { albums: GalleryEvent[] }) {
  const sessionText = useSyncExternalStore(() => () => {}, sessionLabel, () => "|Member");
  const sessionName = sessionText.slice(0, sessionText.indexOf("|"));
  const [profileName, setProfileName] = useState("");
  const [memberId, setMemberId] = useState("");
  const [donations, setDonations] = useState<Array<{ id: number; occasion: string; event_name: string; amount: number; status: string; donation_date: string }> | null>(null);
  const [donationTotal, setDonationTotal] = useState<number | null>(null);
  const name = profileName || sessionName;

  useEffect(() => {
    let cancelled = false;
    fetch("http://localhost:5002/api/profile", { credentials: "include" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data?.user) return;
        const user = data.user as ProfileUser;
        const nextName = String(user.full_name || user.username || "").trim();
        if (nextName) setProfileName(nextName);
        if (user.username) setMemberId(user.username);
        else if (user.id) setMemberId(String(user.id));
      })
      .catch(() => {});
    fetch("http://localhost:5002/api/donations/mine", { credentials: "include" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.donations)) return;
        setDonations(data.donations);
        setDonationTotal(Number(data.totals?.all) || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const photoCount = albums.reduce((sum, album) => sum + album.images.length, 0);
  const recent = albums.slice(0, 3);

  return (
    <>
      <style>{`
        .md-wrap { width: min(1200px, calc(100% - 40px)); margin: 0 auto; padding: 22px 0 40px; }
        .md-hero {
          display: flex; justify-content: space-between; gap: 20px; align-items: center;
          background: #fff; border: 1px solid #EFE4D6; border-radius: 18px;
          padding: 22px 26px; box-shadow: 0 10px 24px rgba(27,42,74,0.04);
          background-image: linear-gradient(90deg, #fff 58%, rgba(255,255,255,0.2)), url("/assets/Agrohadham/1.webp");
          background-size: auto, 280px;
          background-repeat: no-repeat;
          background-position: left center, right center;
        }
        .md-hero h1 { margin: 0; font-family: var(--font-head); font-size: clamp(28px, 3vw, 36px); color: var(--md-navy); }
        .md-hero p { margin: 8px 0 0; color: #6F6258; }
        .md-cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin-top: 16px; }
        .md-card, .md-panel {
          background: #fff; border: 1px solid #EFE4D6; border-radius: 16px;
          box-shadow: 0 8px 20px rgba(27,42,74,0.04);
        }
        .md-card { padding: 18px 18px 16px; }
        .md-ico { width: 42px; height: 42px; border-radius: 12px; display: grid; place-items: center; background: #FFF1E8; color: var(--md-orange); margin-bottom: 12px; }
        .md-card span { display: block; color: #6F6258; font-size: 14px; font-weight: 600; }
        .md-card strong { display: block; margin-top: 4px; font-family: var(--font-head); font-size: 30px; color: var(--md-navy); }
        .md-card strong.md-money { font-family: var(--font-body); font-weight: 700; font-size: 28px; }
        .md-card small { display: block; margin-top: 4px; color: #8D7568; }
        .md-split { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 16px; margin-top: 16px; }
        .md-panel { padding: 16px 16px 8px; }
        .md-panel header, .md-row { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
        .md-panel h2 { margin: 0; font-family: var(--font-head); font-size: 22px; }
        .md-panel header a { color: var(--md-orange); font-weight: 700; text-decoration: none; font-size: 14px; }
        .md-event {
          display: grid; grid-template-columns: 72px minmax(0, 1fr); gap: 12px;
          padding: 12px 0; border-top: 1px solid #F3E8DC; text-decoration: none; color: inherit;
          align-items: center;
        }
        .md-gift {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 12px 16px;
          padding: 12px 0;
          border-top: 1px solid #F3E8DC;
          text-decoration: none;
          color: inherit;
          align-items: start;
        }
        .md-gift > span {
          min-width: 0;
        }
        .md-event img, .md-thumb {
          width: 72px; height: 56px; object-fit: cover; border-radius: 10px; background: #FFF1E8;
        }
        .md-event strong, .md-gift strong {
          display: block;
          color: var(--md-navy);
          font-size: 14px;
          line-height: 1.35;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .md-event small, .md-gift small {
          display: block;
          margin-top: 4px;
          color: #8D7568;
          font-size: 12px;
          line-height: 1.4;
          white-space: normal;
        }
        .md-gift b {
          flex: none;
          white-space: nowrap;
          font-family: var(--font-body);
          font-weight: 700;
          font-size: 15px;
          color: var(--md-navy);
          line-height: 1.35;
          padding-top: 1px;
        }
        .md-empty { padding: 16px 0 12px; color: #8D7568; }
        @media (max-width: 900px) {
          .md-cards, .md-split { grid-template-columns: 1fr; }
          .md-wrap { width: calc(100% - 24px); }
          .md-hero { background-size: auto, 140px; padding: 18px; }
        }
      `}</style>

      <div className="md-wrap">
        <section className="md-hero">
          <div>
            <h1>Welcome, {name || "Member"}</h1>
            <p>
              {memberId ? `Member ID: ${memberId}` : "Member account"}
              {" | Keep supporting our community."}
            </p>
          </div>
        </section>

        <section className="md-cards">
          <article className="md-card">
            <div className="md-ico" aria-hidden="true">♥</div>
            <span>Total Donations</span>
            <strong className="md-money">{donationTotal === null ? "—" : `₹${donationTotal.toLocaleString("en-IN")}`}</strong>
            <small>{donations ? `${donations.length} contributions` : "Loading your donations"}</small>
          </article>
          <article className="md-card">
            <div className="md-ico" aria-hidden="true">▣</div>
            <span>Events</span>
            <strong>{albums.length}</strong>
            <small>Community Events</small>
          </article>
          <article className="md-card">
            <div className="md-ico" aria-hidden="true">▦</div>
            <span>Photos in Gallery</span>
            <strong>{photoCount}</strong>
            <small>Event Photos</small>
          </article>
        </section>

        <section className="md-split">
          <article className="md-panel" id="events">
            <header>
              <h2>Recent Events</h2>
              <Link href="/events">View All →</Link>
            </header>
            {recent.length ? recent.map((event) => (
              <Link key={event.folderName} href="/gallery" className="md-event">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={event.cover} alt="" />
                <span>
                  <strong>{event.title}</strong>
                  <small>{event.images.length} photos</small>
                </span>
              </Link>
            )) : <p className="md-empty">No community events are published yet.</p>}
          </article>
          <article className="md-panel" id="donations">
            <header>
              <h2>My Recent Donations</h2>
              <button type="button" onClick={() => openDonateModal()} style={{ border: "none", background: "transparent", color: "#F4510B", fontWeight: 700, cursor: "pointer" }}>
                Donate →
              </button>
            </header>
            {donations && donations.length ? donations.slice(0, 4).map((row) => (
              <Link key={row.id} href="/m/donations" className="md-gift">
                <span>
                  <strong>{row.event_name || row.occasion || "Donation"}</strong>
                  <small>
                    {row.donation_date}
                    {" · "}
                    {row.status === "verified" ? "Verified" : row.status === "rejected" ? "Rejected" : "Pending"}
                  </small>
                </span>
                <b>₹{Number(row.amount || 0).toLocaleString("en-IN")}</b>
              </Link>
            )) : <p className="md-empty">{donations ? "No donations found" : "Loading your donations…"}</p>}
          </article>
        </section>
      </div>
    </>
  );
}
